import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Global axios defaults - 15 second timeout for all requests
axios.defaults.timeout = 15000;

const AuthContext = createContext(null);

// Role hierarchy: employee < manager < admin < director (directors have highest access)
const ROLE_LEVELS = { employee: 1, user: 1, manager: 2, admin: 3, director: 4 };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needs2FASetup, setNeeds2FASetup] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const IDLE_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes of inactivity = auto logout

  const forceLogout = useCallback((message) => {
    setToken(null);
    setUser(null);
    sessionStorage.removeItem("auth_token");
    sessionStorage.removeItem("auth_user");
    delete axios.defaults.headers.common["Authorization"];
    if (message) {
      alert(message);
    }
    window.location.href = "/login";
  }, []);

  // Global axios interceptor - auto-logout on 401, detect 2FA enforcement on 403, handle timeouts & rate limits
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Handle network timeouts
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          console.error("[Axios] Request timed out:", error.config?.url);
          error.userMessage = "Request timed out. Please check your connection and try again.";
          return Promise.reject(error);
        }
        
        // Handle rate limiting (429)
        if (error.response?.status === 429) {
          const detail = error.response?.data?.detail || "Too many requests. Please slow down.";
          error.userMessage = detail;
          return Promise.reject(error);
        }
        
        if (error.response?.status === 401) {
          const detail = error.response?.data?.detail || "";
          if (detail.includes("revoked") || detail.includes("locked") || detail.includes("disabled")) {
            forceLogout(`Security Alert: ${detail}`);
            return new Promise(() => {}); // Stop the chain
          }
        }
        // Detect mandatory 2FA enforcement from backend
        // Skip if we're on the login page (login flow handles 2FA itself)
        if (error.response?.status === 403) {
          const detail = error.response?.data?.detail || "";
          const isLoginPage = window.location.pathname === "/login";
          if (detail.includes("2FA setup required") && !isLoginPage) {
            setNeeds2FASetup(true);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [forceLogout]);

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = sessionStorage.getItem("auth_token");
      const savedUser = sessionStorage.getItem("auth_user");
      const saved2FA = sessionStorage.getItem("needs_2fa_setup");
      
      if (savedToken && savedUser) {
        try {
          const parsedUser = JSON.parse(savedUser);
          setToken(savedToken);
          setUser(parsedUser);
          if (saved2FA === "true") setNeeds2FASetup(true);
          axios.defaults.headers.common["Authorization"] = `Bearer ${savedToken}`;
          
          // Refresh user data from server to sync role changes
          try {
            const res = await axios.get(`${API}/auth/me`);
            const fresh = res.data;
            // Check if user now has 2FA enabled (completed setup)
            if (fresh?.totp_enabled) {
              setNeeds2FASetup(false);
              sessionStorage.removeItem("needs_2fa_setup");
            }
            if (fresh && fresh.role !== parsedUser.role) {
              setUser(fresh);
              sessionStorage.setItem("auth_user", JSON.stringify(fresh));
            }
          } catch (e) {
            // Token expired - clear auth
            if (e.response?.status === 401) {
              sessionStorage.removeItem("auth_token");
              sessionStorage.removeItem("auth_user");
              sessionStorage.removeItem("needs_2fa_setup");
              setToken(null);
              setUser(null);
              delete axios.defaults.headers.common["Authorization"];
            }
          }
        } catch (e) {
          sessionStorage.removeItem("auth_token");
          sessionStorage.removeItem("auth_user");
        }
      }
      setLoading(false);
    };
    
    initAuth();
  }, []);

  // Periodic session validation & idle detection - check every 60 seconds
  useEffect(() => {
    if (!token) return;
    
    // Idle Activity Listeners
    const updateActivity = () => { lastActivityRef.current = Date.now(); };
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    activityEvents.forEach(evt => window.addEventListener(evt, updateActivity, { passive: true }));
    
    const interval = setInterval(async () => {
      // 1. Check Idle Timeout
      if (Date.now() - lastActivityRef.current > IDLE_TIMEOUT_MS) {
        forceLogout("Session expired due to 10 minutes of inactivity.");
        return;
      }
      
      // 2. Validate token viability with backend
      try {
        await axios.get(`${API}/auth/me`);
      } catch (e) {
        // Will be caught by the 401 interceptor above
      }
    }, 60000);
    
    return () => {
      clearInterval(interval);
      activityEvents.forEach(evt => window.removeEventListener(evt, updateActivity));
    };
  }, [token, forceLogout]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData, needs_2fa_setup } = response.data;
    
    setToken(access_token);
    setUser(userData);
    setNeeds2FASetup(!!needs_2fa_setup);
    sessionStorage.setItem("auth_token", access_token);
    sessionStorage.setItem("auth_user", JSON.stringify(userData));
    if (needs_2fa_setup) sessionStorage.setItem("needs_2fa_setup", "true");
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
    
    return userData;
  }, []);

  const register = useCallback(async (email, password, name, role = "employee") => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, role });
    const { access_token, user: userData } = response.data;
    
    setToken(access_token);
    setUser(userData);
    sessionStorage.setItem("auth_token", access_token);
    sessionStorage.setItem("auth_user", JSON.stringify(userData));
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
    
    return userData;
  }, []);

  const complete2FASetup = useCallback(() => {
    setNeeds2FASetup(false);
    sessionStorage.removeItem("needs_2fa_setup");
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setNeeds2FASetup(false);
    sessionStorage.removeItem("auth_token");
    sessionStorage.removeItem("auth_user");
    sessionStorage.removeItem("needs_2fa_setup");
    delete axios.defaults.headers.common["Authorization"];
  }, []);

  const role = user?.role || "employee";
  const roleLevel = ROLE_LEVELS[role] || 1;
  
  const isEmployee = roleLevel >= 1;
  const isManager = roleLevel >= 2;
  const isAdmin = roleLevel >= 3; // admin AND director
  const isDirector = roleLevel >= 4; // only director

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token,
    needs2FASetup,
    role,
    roleLevel,
    isEmployee,
    isManager,
    isDirector,
    isAdmin,
    login,
    register,
    logout,
    setUser,
    complete2FASetup,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
